/**
 * The one named contract for every survey authoring setting (spec 42, #701).
 *
 * Declared ONCE, and the declaration is load-bearing: every key is REQUIRED, so a caller must say
 * something about each one (`undefined` = "leave it alone", but written down); the key list is a
 * mapped type, so adding a field without declaring it does not compile; and `restrictedOn` names
 * the surfaces where a setting may not be CHANGED, with the sentence shown and whether the control
 * is hidden or locked. Neither may mean silently absent.
 *
 * Why it replaced two positional callbacks: decisions-02, 2026-08-18T11:45.
 */
import type { SurveyBlockDefinition } from '../../utils/surveyApi';
import type { SurveyTimerConfig } from './surveyTimer';
// Type-only, so the cycle with the editor is erased at compile time. `RedirectConfig` lives with
// the component that owns the redirect form; moving it is PR 7's extraction, not this contract's.
import type { RedirectConfig } from './SurveyQuestionsPreview';

/**
 * The authoring surfaces, and the request each one saves through.
 *
 * `create`      → POST /api/surveys                          (SurveyBuilder)
 * `survey_edit` → PUT  /api/surveys/{id}/questions           (SurveyDetails)
 * `group_edit`  → PUT  /api/survey-groups/{id}/questions     (SurveyGroupDetails)
 */
export type SurveyAuthoringSurface = 'create' | 'survey_edit' | 'group_edit';

/**
 * Every field name the backend's `SurveyUpdate` accepts (`schemas/survey.py`).
 *
 * Checked in rather than fetched: cross-repo CI wiring is out of scope (BluePill-AI/backend#832).
 * Typing `apiField` against this union is what stops a setting from naming a field the server has
 * never heard of — the drop would otherwise be a 200 with nothing stored.
 */
export const SURVEY_UPDATE_FIELDS = [
  'questions',
  'blocks',
  'timer_config',
  'max_responses',
  'redirect_config',
  'enable_ai_redundancy_skip',
  'redundancy_question_mode',
  'followup_suppression_enabled',
  'survey_level_probing_enabled',
  'adaptive_probing',
  'cross_question_coverage',
  'adaptive_question_scoping',
  'survey_style',
  'discussion_guide',
  'acknowledge_warnings',
  'min_linkedin_connections',
] as const;

export type SurveyUpdateField = (typeof SURVEY_UPDATE_FIELDS)[number];

/**
 * What the editor hands its parent, and what the parent must persist.
 *
 * Every key is required so a caller must say something about each. `null` and `undefined` are NOT
 * synonyms: `timer_config: null` CLEARS a timer, `undefined` leaves the stored one alone.
 */
export interface SurveyAuthoringSettings {
  blocks: SurveyBlockDefinition[] | null | undefined;
  redirect_config: RedirectConfig | null | undefined;
  max_responses: number | null | undefined;
  min_linkedin_connections: number | null | undefined;
  timer_config: SurveyTimerConfig | null | undefined;
  adaptive_probing: boolean | undefined;
  survey_level_probing_enabled: boolean | undefined;
}

export type SurveyAuthoringSettingKey = keyof SurveyAuthoringSettings;

/** Where the author configures a control — which region of the editor it lives in. */
export type SurveyAuthoringSettingControl = 'settings-form' | 'question-list' | 'create-form';

/**
 * How a surface presents a setting the author may not change: `hidden` removes the control,
 * `locked` shows it disabled. Both MUST carry a reason — neither may mean silently absent.
 */
export type SurveyAuthoringRestrictionMode = 'hidden' | 'locked';

export interface SurveyAuthoringRestriction {
  readonly mode: SurveyAuthoringRestrictionMode;
  /** The sentence the author is SHOWN. Never optional: silence is the defect being fixed. */
  readonly reason: string;
}

/** What every authoring control declares, whether or not its value travels in the save payload. */
export interface SurveyAuthoringControlSpec {
  readonly control: SurveyAuthoringSettingControl;
  /** Surfaces where the author may not change this, mapped to how they are told. */
  readonly restrictedOn: Readonly<
    Partial<Record<SurveyAuthoringSurface, SurveyAuthoringRestriction>>
  >;
}

export interface SurveyAuthoringSettingSpec extends SurveyAuthoringControlSpec {
  /** The `SurveyUpdate` field this maps to. Deliberately the same vocabulary as the backend. */
  readonly apiField: SurveyUpdateField;
}

/** Said in place of an `ai_features` control before the survey exists to attach it to. */
const AI_FEATURES_NEED_A_SAVED_SURVEY: SurveyAuthoringRestriction = {
  mode: 'hidden',
  reason:
    'Save the survey first, then configure this here. Creating a survey does not carry the AI ' +
    'settings, so a switch here would not be stored.',
};

/**
 * Said in place of a group control the group save request would drop. Forwarding an un-prefilled
 * form here would CLEAR the stored value — for the LinkedIn gate that silently opens a survey to
 * everyone. decisions-02, 2026-08-19.
 */
const GROUPS_DO_NOT_CARRY_YET: SurveyAuthoringRestriction = {
  mode: 'hidden',
  reason: 'Not available for survey groups yet — set this on each survey in the group.',
};

/**
 * The LinkedIn gate is hidden on EVERY surface (2026-08-20). The group create route declares
 * `min_linkedin_connections` and never passes it to `create_survey`, so an author sets a threshold,
 * gets a 201, and no gate is applied — and the editor reads null back, so nothing shows it failed.
 * Hiding the control is what stops a new survey being configured with a gate that does not hold;
 * the respondent-side /linkedin/* verification routes stay live for surveys already fielded.
 */
const LINKEDIN_GATE_TEMPORARILY_OFF: SurveyAuthoringRestriction = {
  mode: 'hidden',
  reason: 'LinkedIn verification is temporarily unavailable.',
};

/**
 * Why the number of groups is not editable — one tense-neutral sentence, so the create form shows
 * it as a warning and both edit surfaces as the explanation. Two would be two places to drift.
 *
 * UNCONDITIONAL by design: it must not consult `responses_count`. Reasoning and the two facts that
 * settle it: decisions-02, 2026-08-18T20:40.
 */
export const GROUP_COUNT_FIXED_AT_CREATION =
  'The number of groups is set once, when the survey is created — changing it later would have to ' +
  're-allocate every quota and decide what happens to answers already collected against a group ' +
  'that disappears.';

const GROUP_COUNT_IS_PERMANENT: SurveyAuthoringRestriction = {
  mode: 'locked',
  reason: GROUP_COUNT_FIXED_AT_CREATION,
};

/**
 * Every setting, declared once.
 *
 * A mapped type over `SurveyAuthoringSettings`, so adding a field to that interface without adding
 * an entry here is a compile error rather than a setting nothing persists.
 */
export const SURVEY_AUTHORING_SETTINGS: {
  readonly [K in SurveyAuthoringSettingKey]: SurveyAuthoringSettingSpec;
} = {
  blocks: {
    apiField: 'blocks',
    control: 'question-list',
    restrictedOn: {},
  },
  redirect_config: {
    apiField: 'redirect_config',
    control: 'settings-form',
    restrictedOn: {},
  },
  max_responses: {
    apiField: 'max_responses',
    control: 'settings-form',
    restrictedOn: {},
  },
  min_linkedin_connections: {
    apiField: 'min_linkedin_connections',
    control: 'settings-form',
    restrictedOn: {
      create: LINKEDIN_GATE_TEMPORARILY_OFF,
      survey_edit: LINKEDIN_GATE_TEMPORARILY_OFF,
      group_edit: LINKEDIN_GATE_TEMPORARILY_OFF,
    },
  },
  timer_config: {
    apiField: 'timer_config',
    control: 'settings-form',
    restrictedOn: { group_edit: GROUPS_DO_NOT_CARRY_YET },
  },
  adaptive_probing: {
    apiField: 'adaptive_probing',
    control: 'settings-form',
    restrictedOn: { create: AI_FEATURES_NEED_A_SAVED_SURVEY },
  },
  survey_level_probing_enabled: {
    apiField: 'survey_level_probing_enabled',
    control: 'settings-form',
    restrictedOn: { create: AI_FEATURES_NEED_A_SAVED_SURVEY },
  },
};

/**
 * A field the author configures while authoring that is NOT part of the settings payload — it
 * travels on its own request (a name) or only at creation (a group count).
 */
export type SurveyAuthoringStructuralKey = 'group_count';

/** Every control the author meets on an authoring surface — settings plus structural fields. */
export type SurveyAuthoringControlKey = SurveyAuthoringSettingKey | SurveyAuthoringStructuralKey;

/**
 * Every authoring control, derived from the settings record rather than restating it.
 *
 * G1 (render parity) iterates THIS, so a structural field that is locked on a surface is covered by
 * the same guard as a setting that is hidden on one — which is what stops `group_count` being a
 * special case that only a hand-written assertion remembers.
 */
export const SURVEY_AUTHORING_CONTROLS: {
  readonly [K in SurveyAuthoringControlKey]: SurveyAuthoringControlSpec;
} = {
  ...SURVEY_AUTHORING_SETTINGS,
  group_count: {
    control: 'create-form',
    restrictedOn: {
      survey_edit: GROUP_COUNT_IS_PERMANENT,
      group_edit: GROUP_COUNT_IS_PERMANENT,
    },
  },
};

export const SURVEY_AUTHORING_CONTROL_KEYS = Object.keys(
  SURVEY_AUTHORING_CONTROLS,
) as readonly SurveyAuthoringControlKey[];

/**
 * Every settings key, derived from the declaration above.
 *
 * Derived rather than hand-written on purpose: a second list could DIVERGE from the record, and
 * then the exhaustiveness the mapped type buys us would be checking the wrong thing.
 */
export const SURVEY_AUTHORING_SETTING_KEYS = Object.keys(
  SURVEY_AUTHORING_SETTINGS,
) as readonly SurveyAuthoringSettingKey[];

/** How this surface restricts the control, or null when the author may change it here. */
export function restrictionOn(
  key: SurveyAuthoringControlKey,
  surface: SurveyAuthoringSurface,
): SurveyAuthoringRestriction | null {
  return SURVEY_AUTHORING_CONTROLS[key].restrictedOn[surface] ?? null;
}

/**
 * Why this surface cannot PERSIST the setting, or null.
 *
 * Only `hidden` answers here, because the editor uses this to decide whether to replace a control
 * with a notice — and a `locked` control must stay on screen, not vanish.
 */
export function unsupportedReason(
  key: SurveyAuthoringSettingKey,
  surface: SurveyAuthoringSurface,
): string | null {
  const restriction = restrictionOn(key, surface);
  return restriction?.mode === 'hidden' ? restriction.reason : null;
}

/** Why this surface shows the control but will not accept a change, or null. */
export function lockedReason(
  key: SurveyAuthoringControlKey,
  surface: SurveyAuthoringSurface,
): string | null {
  const restriction = restrictionOn(key, surface);
  return restriction?.mode === 'locked' ? restriction.reason : null;
}

/**
 * Whether this surface may send the setting at all.
 *
 * Both modes answer false: a hidden setting has nowhere to be set, and a locked value must not be
 * written back either — a save carrying it would either be dropped or overwrite the fixed choice.
 */
export function isSupportedOn(
  key: SurveyAuthoringSettingKey,
  surface: SurveyAuthoringSurface,
): boolean {
  return restrictionOn(key, surface) === null;
}

/** The keys a surface is expected to send. G2 asserts the request body against exactly these. */
export function settingKeysFor(
  surface: SurveyAuthoringSurface,
): readonly SurveyAuthoringSettingKey[] {
  return SURVEY_AUTHORING_SETTING_KEYS.filter((key) => isSupportedOn(key, surface));
}

/**
 * A payload that touches nothing.
 *
 * The starting point for a structural edit (adding, reordering or re-labelling questions and
 * blocks), which must not carry the settings form's idea of the other values: on the edit surfaces
 * that emit saves immediately, a form field that has not finished loading would be written as the
 * author's choice.
 */
export function noSettingsTouched(): SurveyAuthoringSettings {
  return {
    blocks: undefined,
    redirect_config: undefined,
    max_responses: undefined,
    min_linkedin_connections: undefined,
    timer_config: undefined,
    adaptive_probing: undefined,
    survey_level_probing_enabled: undefined,
  };
}
